import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  CreditCard, 
  Search, 
  Filter, 
  CheckCircle, 
  XCircle,
  Clock,
  Eye,
  Download,
  Mail,
  Phone,
  Calendar,
  FileText,
  DollarSign,
  RefreshCw
} from 'lucide-react';

const AdminPayments = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [offerLetterLoading, setOfferLetterLoading] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'All Payments' },
    { value: 'paid', label: 'Pending Verification' },
    { value: 'verified', label: 'Verified' },
    { value: 'rejected', label: 'Rejected' }
  ];

  useEffect(() => {
    fetchPendingPayments();
  }, []);

  const fetchPendingPayments = async () => {
    try {
      const response = await axios.get('/api/admin/pending-payments');
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPayment = async (studentId, status, remarks = '') => {
    try {
      setVerificationLoading(true);
      await axios.post('/api/admin/verify-payment', {
        studentId,
        status,
        remarks
      });
      
      toast.success(`Payment ${status} successfully!`);
      setShowReceiptModal(false);
      await fetchPendingPayments();
    } catch (error) {
      console.error('Error verifying payment:', error);
      toast.error('Failed to verify payment');
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleDownloadReceipt = async (studentId, filename) => {
    try {
      setDownloadLoading(true);
      const response = await axios.get(`/api/payments/download-receipt/${studentId}`);
      
      if (response.data.receiptUrl) {
        // Create a link to download from Cloudinary URL
        const link = document.createElement('a');
        link.href = response.data.receiptUrl;
        link.setAttribute('download', filename);
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        toast.success('Receipt download started!');
      } else {
        toast.error('Receipt URL not found');
      }
    } catch (error) {
      console.error('Error downloading receipt:', error);
      toast.error('Failed to download receipt');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleViewReceipt = async (studentId, filename) => {
    try {
      const response = await axios.get(`/api/payments/download-receipt/${studentId}`);
      
      if (response.data.receiptUrl) {
        // Open Cloudinary URL in new window
        window.open(response.data.receiptUrl, '_blank');
      } else {
        toast.error('Receipt URL not found');
      }
      
    } catch (error) {
      console.error('Error viewing receipt:', error);
      toast.error('Failed to view receipt');
    }
  };

  const handleViewOfferLetter = async (studentId) => {
    try {
      setOfferLetterLoading(true);
      const response = await axios.get(`/api/admin/offer-letter/${studentId}`);
      
      if (response.data.offerLetterUrl) {
        // Open Cloudinary URL in new window
        window.open(response.data.offerLetterUrl, '_blank');
      } else {
        toast.error('Offer letter URL not found');
      }
      
    } catch (error) {
      console.error('Error viewing offer letter:', error);
      toast.error('Failed to view offer letter');
    } finally {
      setOfferLetterLoading(false);
    }
  };

  const handleRegenerateOfferLetter = async (studentId) => {
    try {
      setOfferLetterLoading(true);
      const response = await axios.post(`/api/admin/regenerate-offer-letter/${studentId}`);
      
      if (response.data.offerLetter) {
        toast.success('Offer letter regenerated successfully!');
        // Refresh the payments list
        fetchPendingPayments();
      }
      
    } catch (error) {
      console.error('Error regenerating offer letter:', error);
      toast.error('Failed to regenerate offer letter');
    } finally {
      setOfferLetterLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Pending Verification' },
      verified: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Verified' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Rejected' }
    };

    const config = statusConfig[status] || statusConfig.paid;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.text}
      </span>
    );
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = payment.userId.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.userId.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || payment.paymentStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payment Management</h1>
            <p className="text-gray-600 mt-1">
              Review and verify student payment receipts
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="bg-primary-50 px-4 py-2 rounded-lg">
              <span className="text-primary-700 font-medium">
                {filteredPayments.filter(p => p.paymentStatus === 'paid').length} Pending
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Export Button */}
          {/* <button className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </button> */}
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Branch & Fee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayments.map((payment) => (
                <tr key={payment._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-primary-600 font-medium text-sm">
                            {payment.userId.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {payment.userId.name}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Mail className="h-3 w-3 mr-1" />
                          {payment.userId.email}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Phone className="h-3 w-3 mr-1" />
                          {payment.userId.phone}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <div className="font-medium mb-1">
                        {payment.allocatedBranch}
                      </div>
                      <div className="flex items-center text-xs text-gray-500">
                        <DollarSign className="h-3 w-3 mr-1" />
                        Fee: ₹3,15,000
                      </div>
                      <div className="text-xs text-gray-500">
                        Rank: #{payment.rank}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {payment.paymentReceipt && (
                        <>
                          <div className="flex items-center mb-1">
                            <FileText className="h-4 w-4 mr-1 text-gray-400" />
                            <span className="font-medium">Receipt Uploaded</span>
                          </div>
                          <div className="flex items-center text-xs text-gray-500">
                            <Calendar className="h-3 w-3 mr-1" />
                            {new Date(payment.paymentReceipt.uploadedAt).toLocaleDateString()}
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(payment.paymentStatus)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setSelectedPayment(payment);
                          setShowReceiptModal(true);
                        }}
                        className="text-primary-600 hover:text-primary-900 p-1 rounded"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {payment.paymentReceipt && (
                        <button
                          onClick={() => handleDownloadReceipt(payment._id, payment.paymentReceipt.filename)}
                          disabled={downloadLoading}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Download Receipt"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      )}
                      {payment.paymentStatus === 'paid' && (
                        <>
                          <button
                            onClick={() => handleVerifyPayment(payment._id, 'verified')}
                            disabled={verificationLoading}
                            className="text-green-600 hover:text-green-900 p-1 rounded"
                            title="Verify Payment"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleVerifyPayment(payment._id, 'rejected')}
                            disabled={verificationLoading}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                            title="Reject Payment"
                          >
                            <XCircle className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPayments.length === 0 && (
          <div className="text-center py-12">
            <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No payments found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search criteria or filters.
            </p>
          </div>
        )}
      </div>

      {/* Receipt Verification Modal */}
      {showReceiptModal && selectedPayment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-2/3 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Payment Verification: {selectedPayment.userId.name}
                </h3>
                <button
                  onClick={() => setShowReceiptModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Student Details */}
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">Student Information</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Name:</span>
                        <span className="font-medium">{selectedPayment.userId.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email:</span>
                        <span className="font-medium">{selectedPayment.userId.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Phone:</span>
                        <span className="font-medium">{selectedPayment.userId.phone}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Branch:</span>
                        <span className="font-medium">{selectedPayment.allocatedBranch}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Rank:</span>
                        <span className="font-medium">#{selectedPayment.rank}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">Payment Details</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Fee Amount:</span>
                        <span className="font-medium">₹3,15,000</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Upload Date:</span>
                        <span className="font-medium">
                          {selectedPayment.paymentReceipt && 
                            new Date(selectedPayment.paymentReceipt.uploadedAt).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span>{getStatusBadge(selectedPayment.paymentStatus)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Receipt Preview */}
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">Payment Receipt</h4>
                    {selectedPayment.paymentReceipt ? (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                        <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <p className="text-sm text-gray-600 mb-2">
                          Receipt: {selectedPayment.paymentReceipt.filename}
                        </p>
                        <div className="flex space-x-3 justify-center">
                          <button 
                            onClick={() => handleViewReceipt(selectedPayment._id, selectedPayment.paymentReceipt.filename)}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Receipt
                          </button>
                          <button 
                            onClick={() => handleDownloadReceipt(selectedPayment._id, selectedPayment.paymentReceipt.filename)}
                            disabled={downloadLoading}
                            className="text-primary-600 hover:text-primary-700 text-sm font-medium disabled:opacity-50 flex items-center"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            {downloadLoading ? 'Downloading...' : 'Download Receipt'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center text-gray-500">
                        <p>No receipt uploaded</p>
                      </div>
                    )}
                  </div>

                  {selectedPayment.paymentStatus === 'paid' && (
                    <div className="space-y-3">
                      <button
                        onClick={() => handleVerifyPayment(selectedPayment._id, 'verified')}
                        disabled={verificationLoading}
                        className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {verificationLoading ? 'Verifying...' : 'Verify Payment'}
                      </button>
                      <button
                        onClick={() => handleVerifyPayment(selectedPayment._id, 'rejected')}
                        disabled={verificationLoading}
                        className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        {verificationLoading ? 'Rejecting...' : 'Reject Payment'}
                      </button>
                    </div>
                  )}

                  {/* Offer Letter Section */}
                  {selectedPayment.paymentStatus === 'verified' && selectedPayment.offerLetter && (
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-medium text-blue-900 mb-3">Offer Letter</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-blue-700">Letter Number:</span>
                          <span className="font-medium">{selectedPayment.offerLetter.letterNumber}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700">Generated:</span>
                          <span className="font-medium">
                            {new Date(selectedPayment.offerLetter.generatedAt).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-3 mt-3">
                        <button 
                          onClick={() => handleViewOfferLetter(selectedPayment._id)}
                          disabled={offerLetterLoading}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium disabled:opacity-50 flex items-center"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          {offerLetterLoading ? 'Loading...' : 'View Offer Letter'}
                        </button>
                        <button 
                          onClick={() => handleRegenerateOfferLetter(selectedPayment._id)}
                          disabled={offerLetterLoading}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium disabled:opacity-50 flex items-center"
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          {offerLetterLoading ? 'Regenerating...' : 'Regenerate'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPayments;